/**
 * Minimal RFC 6902 JSON Patch applier. Mirrors the backend's
 * app/services/jsonpatch.py — only replace/add/remove supported.
 * Pointer syntax (RFC 6901) with ~0/~1 escapes; `-` as a list index appends.
 *
 * Mutates the input in place; returns the same reference for chaining.
 */
export class DiffApplyError extends Error {}

export interface PatchOp {
  op: 'replace' | 'add' | 'remove' | string;
  path: string;
  value?: unknown;
}

function unescape(token: string): string {
  return token.replace(/~1/g, '/').replace(/~0/g, '~');
}

function parsePointer(pointer: string): string[] {
  if (pointer === '') return [];
  if (!pointer.startsWith('/')) throw new DiffApplyError(`bad pointer: ${pointer}`);
  return pointer.split('/').slice(1).map(unescape);
}

function navigate(doc: unknown, tokens: string[]): { parent: unknown; last: string | number } {
  if (tokens.length === 0) throw new DiffApplyError('cannot operate on root pointer');
  let parent: unknown = doc;
  for (let i = 0; i < tokens.length - 1; i++) {
    const tok = tokens[i]!;
    if (Array.isArray(parent)) {
      const idx = Number(tok);
      if (!Number.isInteger(idx)) throw new DiffApplyError(`bad list index ${tok}`);
      parent = parent[idx];
    } else if (parent && typeof parent === 'object') {
      const obj = parent as Record<string, unknown>;
      if (!(tok in obj)) throw new DiffApplyError(`missing key ${tok}`);
      parent = obj[tok];
    } else {
      throw new DiffApplyError(`cannot traverse ${typeof parent} at ${tok}`);
    }
  }
  const last = tokens[tokens.length - 1]!;
  if (Array.isArray(parent)) {
    if (last === '-') return { parent, last };
    const idx = Number(last);
    // Mirror the backend: a malformed final token must throw DiffApplyError
    // (caught → banner + keep prior snapshot) — `arr[NaN] = v` would instead
    // silently corrupt the document and skip the resync path.
    if (!Number.isInteger(idx) || idx < 0) {
      throw new DiffApplyError(`bad list index ${last} in path`);
    }
    return { parent, last: idx };
  }
  return { parent, last };
}

export function applyPatch<T>(doc: T, patch: PatchOp[]): T {
  if (!Array.isArray(patch)) throw new DiffApplyError('patch must be an array');
  for (const op of patch) {
    if (!op || typeof op !== 'object') throw new DiffApplyError('patch op must be an object');
    if (typeof op.path !== 'string') throw new DiffApplyError('patch op missing path');
    const tokens = parsePointer(op.path);
    if (op.op === 'replace' || op.op === 'add') {
      if (tokens.length === 0) {
        if (doc && typeof doc === 'object' && !Array.isArray(doc) && op.value && typeof op.value === 'object') {
          const root = doc as Record<string, unknown>;
          for (const k of Object.keys(root)) delete root[k];
          Object.assign(root, op.value as Record<string, unknown>);
          continue;
        }
        throw new DiffApplyError('root-level replace/add only supported for object roots');
      }
      const { parent, last } = navigate(doc, tokens);
      if (Array.isArray(parent)) {
        const arr = parent;
        if (last === '-') {
          // RFC 6902 only defines `-` for add; the backend applier treats a
          // replace at `-` as "replace the final element" — mirror that.
          if (op.op === 'replace') {
            if (arr.length === 0) throw new DiffApplyError('replace at - on empty array');
            arr[arr.length - 1] = op.value;
          } else {
            arr.push(op.value);
          }
        } else if (op.op === 'replace') {
          if ((last as number) >= arr.length) {
            throw new DiffApplyError(`replace index ${String(last)} out of range`);
          }
          arr[last as number] = op.value;
        } else {
          arr.splice(last as number, 0, op.value);
        }
      } else if (parent && typeof parent === 'object') {
        const obj = parent as Record<string, unknown>;
        if (op.op === 'replace' && !(String(last) in obj)) {
          throw new DiffApplyError(`replace on missing key ${String(last)}`);
        }
        obj[String(last)] = op.value;
      } else {
        throw new DiffApplyError(`cannot ${op.op} into ${typeof parent}`);
      }
    } else if (op.op === 'remove') {
      const { parent, last } = navigate(doc, tokens);
      if (Array.isArray(parent)) {
        // Backend parity (app/services/jsonpatch.py): `-` is add-only and an
        // out-of-range index raises DiffApplyError there. splice would instead
        // silently remove [0] (splice(NaN) → splice(0)) or no-op — corrupting
        // the mirror without triggering the resync path.
        if (last === '-' || (last as number) >= parent.length) {
          throw new DiffApplyError(`bad list index ${String(last)} for remove`);
        }
        parent.splice(last as number, 1);
      } else if (parent && typeof parent === 'object') {
        const obj = parent as Record<string, unknown>;
        if (!(String(last) in obj)) throw new DiffApplyError(`remove on missing key ${String(last)}`);
        delete obj[String(last)];
      } else {
        throw new DiffApplyError(`cannot remove from ${typeof parent}`);
      }
    } else {
      throw new DiffApplyError(`unsupported op ${op.op}`);
    }
  }
  return doc;
}
